import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Slide,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { Close, GetApp, Star } from '@mui/icons-material';
import { detectPlatform, getAppDownloadLink } from '../utils/platformDetection';

const GooglePlayBanner: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [hasSeenBanner, setHasSeenBanner] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Detect platform and get appropriate app info
    const platformInfo = detectPlatform();
    const appInfo = getAppDownloadLink(platformInfo);

    useEffect(() => {
        // Only show banner on mobile devices
        if (!platformInfo.isMobile) return;
        
        // Check if user has already seen/dismissed the banner
        const bannerDismissed = localStorage.getItem('appDownloadBannerDismissed');
        const lastShown = localStorage.getItem('appDownloadBannerLastShown');
        const now = Date.now();
        const oneWeek = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds

        // Show banner if:
        // 1. Never dismissed OR
        // 2. Dismissed more than a week ago AND on mobile
        if (!bannerDismissed || (lastShown && (now - parseInt(lastShown)) > oneWeek)) {
            // Delay showing banner by 3 seconds for better UX
            const timer = setTimeout(() => {
                setIsVisible(true);
                localStorage.setItem('appDownloadBannerLastShown', now.toString());
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [platformInfo.isMobile]);

    const handleClose = () => {
        setIsVisible(false);
        localStorage.setItem('appDownloadBannerDismissed', 'true');
        localStorage.setItem('appDownloadBannerLastShown', Date.now().toString());
    };

    const handleDownload = () => {
        // Track download click with platform-specific label
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'click', {
                event_category: 'app_download',
                event_label: platformInfo.isIOS ? 'testflight_banner' : 'google_play_banner',
                value: 1
            });
        }
        
        window.open(appInfo.url, '_blank');
        
        // Hide banner after download click
        setTimeout(() => {
            handleClose();
        }, 1000);
    };

    return (
        <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: '#000',
                    color: 'white',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    zIndex: 1300,
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                {/* Content */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 2 },
                        flex: 1,
                        minWidth: 0
                    }}
                >
                    {/* App Store Icon */}
                    {platformInfo.isIOS ? (
                        // TestFlight Icon
                        <Box
                            component="svg"
                            viewBox="0 0 24 24"
                            sx={{
                                width: { xs: 24, sm: 28 },
                                height: { xs: 24, sm: 28 },
                                flexShrink: 0,
                                display: { xs: 'none', sm: 'block' }
                            }}
                        >
                            <path
                                fill="#007AFF"
                                d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6L15.5,10.5L12,15L8.5,10.5L12,6Z"
                            />
                        </Box>
                    ) : (
                        // Google Play Icon
                        <Box
                            component="svg"
                            viewBox="0 0 24 24"
                            sx={{
                                width: { xs: 24, sm: 28 },
                                height: { xs: 24, sm: 28 },
                                flexShrink: 0,
                                display: { xs: 'none', sm: 'block' }
                            }}
                        >
                            <path
                                fill="#4285F4"
                                d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5Z"
                            />
                            <path
                                fill="#34A853"
                                d="M16.81,15.12L6.05,21.34C5.79,21.5 5.50,21.57 5.21,21.57C4.60,21.57 4.03,21.26 3.84,21.85L13.69,12L16.81,15.12Z"
                            />
                            <path
                                fill="#FBBC05"
                                d="M16.81,8.88L13.69,12L3.84,2.15C4.03,1.74 4.60,1.43 5.21,1.43C5.50,1.43 5.79,1.50 6.05,1.66L16.81,8.88Z"
                            />
                            <path
                                fill="#EA4335"
                                d="M16.81,15.12C17.96,14.47 20.69,12.81 20.69,12.81C21.82,12.09 21.82,11.91 20.69,11.19L16.81,8.88L13.69,12L16.81,15.12Z"
                            />
                        </Box>
                    )}

                    {/* Text */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                fontWeight: 600,
                                mb: 0.5
                            }}
                        >
                            {isMobile ? appInfo.text : 'Download the Mobile App'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {platformInfo.isIOS ? (
                                // Beta testing indicator for iOS
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: '#007AFF'
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                            opacity: 0.9
                                        }}
                                    >
                                        {appInfo.description}
                                    </Typography>
                                </Box>
                            ) : (
                                // Star rating for Android
                                <>
                                    <Star sx={{ fontSize: 14, color: '#FFD700' }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                            opacity: 0.9
                                        }}
                                    >
                                        {appInfo.rating}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>

                    {/* Download Button */}
                    <Button
                        variant="contained"
                        size={isMobile ? 'small' : 'medium'}
                        startIcon={<GetApp />}
                        onClick={handleDownload}
                        sx={{
                            bgcolor: platformInfo.isIOS ? '#007AFF' : '#4285F4',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: { xs: 2, sm: 3 },
                            py: { xs: 0.5, sm: 1 },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            minWidth: 'auto',
                            flexShrink: 0,
                            '&:hover': {
                                bgcolor: platformInfo.isIOS ? '#0056CC' : '#3367D6',
                                transform: 'translateY(-1px)',
                                boxShadow: platformInfo.isIOS 
                                    ? '0 4px 12px rgba(0, 122, 255, 0.3)'
                                    : '0 4px 12px rgba(66, 133, 244, 0.3)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isMobile ? appInfo.buttonText : appInfo.fullButtonText}
                    </Button>
                </Box>

                {/* Close Button */}
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        color: 'white',
                        flexShrink: 0,
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <Close sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
            </Box>
        </Slide>
    );
};

export default GooglePlayBanner;